import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
} from "antd";
import {
  UserOutlined,
  ArrowRightOutlined,
  SyncOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import moment from "moment";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const BusDashboard = () => {
  const { request } = useRequest();
  const [stats, setStats] = useState({
    totalPassengers: 0,
    oneWayCount: 0,
    roundTripCount: 0,
  });
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [airlines, setAirlines] = useState([]);

  const [filters, setFilters] = useState({
    airline: undefined,
    date: undefined,
    tripType: undefined,
  });

  const fetchStats = () => {
    setStatsLoading(true);
    request({
      url: apiPath.busDashboard,
      method: "GET",
      onSuccess: (data) => {
        setStats(data.stats);
        setStatsLoading(false);
      },
      onError: () => setStatsLoading(false),
    });
  };

  const fetchPassengers = () => {
    setLoading(true);
    const params = {};
    if (filters.airline) params.airline = filters.airline;
    if (filters.tripType) params.tripType = filters.tripType;
    if (filters.date) params.date = filters.date.format("YYYY-MM-DD");

    request({
      url: apiPath.busPassengers,
      method: "GET",
      params,
      onSuccess: (data) => {
        setPassengers(data.passengers);
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  };

  const fetchAirlines = () => {
    request({
      url: apiPath.getAirlines,
      method: "GET",
      onSuccess: (data) => {
        setAirlines(data.airlines || []);
      },
    });
  };

  useEffect(() => {
    fetchStats();
    fetchAirlines();
  }, []);

  useEffect(() => {
    fetchPassengers();
  }, [filters]);

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filters.airline) params.append("airline", filters.airline);
    if (filters.date) params.append("date", filters.date.format("YYYY-MM-DD"));
    if (filters.tripType) params.append("tripType", filters.tripType);

    const url = `${apiPath.baseURL}${apiPath.busExport}?${params.toString()}`;
    const token = localStorage.getItem("token");

    // Using fetch to handle download with auth header
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `transport_list_${moment().format("YYYYMMDD")}.xlsx`;
        link.click();
      })
      .catch((err) => ShowToast("Export failed", Severty.ERROR));
  };

  const columns = [
    {
      title: "Passenger Name",
      dataIndex: "passengerName",
      key: "passengerName",
    },
    {
      title: "Contact Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
    },
    {
      title: "Airline",
      dataIndex: "airlineName",
      key: "airlineName",
    },
    {
      title: "Trip Type",
      dataIndex: "tripType",
      key: "tripType",
      render: (type) => (
        <span style={{ textTransform: "capitalize" }}>
          {type ? type.replace("_", " ") : "N/A"}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => moment(date).format("DD-MM-YYYY HH:mm"),
    },
  ];

  return (
    // <Main>
    <div className="layout-content">
      <Row gutter={[24, 18]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <Card bordered={false} className="criclebox">
            <Statistic
              title="Total Passengers using transport"
              value={stats.totalPassengers}
              loading={statsLoading}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <Card bordered={false} className="criclebox">
            <Statistic
              title="One-way bookings count"
              value={stats.oneWayCount}
              loading={statsLoading}
              prefix={<ArrowRightOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <Card bordered={false} className="criclebox">
            <Statistic
              title="Round-trip bookings count"
              value={stats.roundTripCount}
              loading={statsLoading}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        bordered={false}
        className="criclebox h-full"
        title={
          <Title level={4} style={{ margin: 0 }}>
            Passenger Transport List
          </Title>
        }
        extra={

          <Space wrap>
            {/* <Select
                    placeholder="Filter by Airline"
                    style={{ width: 200 }}
                    allowClear
                    onChange={(val) => setFilters({ ...filters, airline: val })}
                  >
                    {airlines.map((a) => (
                      <Select.Option key={a._id} value={a._id}>
                        {a.name}
                      </Select.Option>
                    ))}
                  </Select> */}
            <DatePicker
              placeholder="Filter by Date"
              onChange={(val) => setFilters({ ...filters, date: val })}
            />
            <Select
              placeholder="Filter by Trip Type"
              style={{ width: 150 }}
              allowClear
              onChange={(val) => setFilters({ ...filters, tripType: val })}
            >
              <Select.Option value="one_way">One-way</Select.Option>
              <Select.Option value="round_trip">Round-trip</Select.Option>
            </Select>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              Export to Excel
            </Button>
          </Space>

        }
      >
        <Table
          columns={columns}
          dataSource={passengers}
          loading={loading}
          rowKey="id"
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
    // </Main>
  );
};

export default BusDashboard;
